import { useState, useCallback, useMemo } from 'react';

/**
 * useColumnFilters — hook for per-column inline filtering
 *
 * Filter value shapes by filterType:
 *   text:     string
 *   select:   string[]  (selected option values)
 *   currency: { min: number|null, max: number|null }
 *   percent:  { min: number|null, max: number|null }
 *   date:     { from: string|null, to: string|null }  (ISO date strings)
 */
export function useColumnFilters() {
  const [filters, setFilters] = useState({});

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilter = useCallback((key) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => isActiveFilter(v)).length;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  /**
   * Apply all active filters to data array using column definitions
   */
  const applyFilters = useCallback((data, columns) => {
    const activeEntries = Object.entries(filters).filter(([, v]) => isActiveFilter(v));
    if (activeEntries.length === 0) return data;

    const columnMap = {};
    columns.forEach(col => { columnMap[col.key] = col; });

    return data.filter(row => {
      return activeEntries.every(([key, filterValue]) => {
        const col = columnMap[key];
        if (!col) return true;
        return matchesFilter(row, key, filterValue, col.filterType);
      });
    });
  }, [filters]);

  return { filters, setFilter, clearFilter, clearAllFilters, applyFilters, hasActiveFilters, activeFilterCount };
}

function isActiveFilter(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return (value.min != null && value.min !== '') ||
           (value.max != null && value.max !== '') ||
           (value.from != null) ||
           (value.to != null);
  }
  return false;
}

function matchesFilter(row, key, filterValue, filterType) {
  const cellValue = row[key];

  switch (filterType) {
    case 'text': {
      if (!filterValue) return true;
      const str = String(cellValue || '').toLowerCase();
      return str.includes(filterValue.toLowerCase());
    }
    case 'select': {
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
      return filterValue.includes(cellValue);
    }
    case 'currency':
    case 'percent': {
      const num = Number(cellValue);
      if (isNaN(num)) return filterValue.min == null && filterValue.max == null ? true : false;
      if (filterValue.min != null && filterValue.min !== '' && num < Number(filterValue.min)) return false;
      if (filterValue.max != null && filterValue.max !== '' && num > Number(filterValue.max)) return false;
      return true;
    }
    case 'date': {
      if (!cellValue) return filterValue.from == null && filterValue.to == null ? true : false;
      const d = new Date(cellValue);
      if (isNaN(d.getTime())) return false;
      if (filterValue.from) {
        const from = new Date(filterValue.from);
        if (d < from) return false;
      }
      if (filterValue.to) {
        const to = new Date(filterValue.to);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      return true;
    }
    default:
      return true;
  }
}
